import React, { useEffect, useState } from 'react';
import { Card, Container, Row } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';
import { get, isEmpty } from 'lodash';
import DynamicFormV2 from 'screens/common/dynamicFormV2';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TransferApi } from 'api/endpoints/Transfer';
import { formSubmissionToast } from 'helpers/utils';
import { getTenantFromToken } from 'utils/routeUtils';

function TransferV2(props) {
  const tenant = getTenantFromToken();
  const navigate = useNavigate();
  const [rawData, setRawData] = useState({});
  const [summary, setSummary] = useState(null);
  const [summaryLoaded, setSummaryLoaded] = useState(false);

  // Merge summary defaultValues into form fields when both are available
  const formData = React.useMemo(() => {
    if (isEmpty(rawData) || !rawData.fields) return rawData;
    if (!summary) return rawData;

    const quantity =
      summary.total_available_quantity || summary.available_quantity;
    const quantityDisplay =
      quantity !== undefined
        ? `${quantity} ${summary.unit_label || ''}`.trim()
        : '';

    const fields = { ...rawData.fields };
    if (fields.crop_id)
      fields.crop_id = { ...fields.crop_id, defaultValue: summary.crop_id };
    if (fields.crop_variety_id)
      fields.crop_variety_id = {
        ...fields.crop_variety_id,
        defaultValue: summary.crop_variety_id
      };
    if (fields.crop_grade_id)
      fields.crop_grade_id = {
        ...fields.crop_grade_id,
        defaultValue: summary.grade_id
      };
    if (fields.source_quantity)
      fields.source_quantity = {
        ...fields.source_quantity,
        defaultValue: quantityDisplay
      };

    return { ...rawData, fields };
  }, [rawData, summary]);

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;

  const procurementsParam = searchParams.get('procurements');

  const fetchSummary = async ids => {
    try {
      const response = await TransferApi.procurementsSummary(ids);
      const results = get(response, 'data.results', {});
      setSummary(Array.isArray(results) ? results[0] : results);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setSummaryLoaded(true);
    }
  };

  const fetchFormData = async () => {
    try {
      const formResponse = await TransferApi.transferForm();
      const results = get(formResponse, 'data.results', {});

      if (procurementsParam && !isEmpty(results.fields)) {
        const firstId = procurementsParam.split(',')[0];
        if (results.fields.source) {
          results.fields.source.endpoint = `/traceability/v1/transfer/source/filtered?procurement_id=${firstId}`;
        }
      }

      setRawData(results);
      console.log('Form fields received:', Object.keys(results.fields || {}));
    } catch (error) {
      console.error('Error fetching form data:', error);
      toast.error('Failed to load form data');
    }
  };

  const fetchEditFormData = async id => {
    try {
      const formResponse = await TransferApi.transferEditForm(id);
      setRawData(formResponse.data.results);
    } catch (error) {
      console.error('Error fetching form data:', error);
      toast.error('Failed to load form data');
    }
  };

  useEffect(() => {
    if (isEditMode) {
      fetchEditFormData(id);
    } else {
      fetchFormData();
      if (procurementsParam) {
        fetchSummary(procurementsParam);
      }
    }
  }, [id, isEditMode, procurementsParam]);
  // Fallback: use window.setFormValue in case source_quantity field was not in the form rules
  // This covers the case where the DB row for source_quantity was not yet cached by the backend
  useEffect(() => {
    if (!summary || !summaryLoaded) return;
    const quantity =
      summary.total_available_quantity || summary.available_quantity;
    const quantityDisplay =
      quantity !== undefined
        ? `${quantity} ${summary.unit_label || ''}`.trim()
        : '';

    const trySet = () => {
      if (window.setFormValue) {
        console.log('Fallback setFormValue source_quantity:', quantityDisplay);
        window.setFormValue('source_quantity', quantityDisplay);
        window.setFormValue('crop_id', summary.crop_id);
        window.setFormValue('crop_variety_id', summary.crop_variety_id);
        window.setFormValue('crop_grade_id', summary.grade_id);
      }
    };

    const t1 = setTimeout(trySet, 300);
    const t2 = setTimeout(trySet, 800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [summary, summaryLoaded]);

  const handleFormSubmit = async formData => {
    try {
      const finalData = { ...formData };

      // Inject specification data from summary if creating
      if (!isEditMode && summary) {
        finalData.crop_id = summary.crop_id;
        finalData.crop_variety_id = summary.crop_variety_id;
        finalData.crop_grade_id = summary.grade_id;
        finalData.source_quantity = summary.total_available_quantity;
      }

      const procurementsData = searchParams.get('procurements');
      if (procurementsData) {
        finalData.crop_details = procurementsData.split(',');
      }

      let response;
      if (isEditMode) {
        response = await TransferApi.updateTransfer(id, finalData);
      } else {
        response = await TransferApi.createTransfer(finalData);
      }

      formSubmissionToast(response, toast, 'Transfer', isEditMode);
      navigate(`/${tenant}/transferReport`);
    } catch (err) {
      formSubmissionToast(err, toast, 'Unexpected error occurred');
    }
  };

  const header = (
    <div className="d-flex align-items-center">
      <div
        className="me-2 my-auto theme-control-toggle"
        onClick={() => navigate(`/${tenant}/transferReport`)}
      >
        <div className="theme-control-toggle-label">
          <FontAwesomeIcon icon={faArrowLeft} className="fs-0" />
        </div>
      </div>
      <span>{isEditMode ? 'Edit Transfer' : 'Create Transfer'}</span>
    </div>
  );

  // Custom fields for Product Specifications
  const getSpecFields = () => {
    if (!summary) return [];
    return [
      {
        sectionTitle: 'Product Specifications',
        fields: [
          {
            label: 'Crop',
            value: summary.crop_label,
            isEditable: false,
            col: 4
          },
          {
            label: 'Variety',
            value: summary.variety_label,
            isEditable: false,
            col: 4
          },
          {
            label: 'Grade',
            value: summary.grade_label,
            isEditable: false,
            col: 4
          },
          {
            name: 'moisture_content',
            label: 'Moisture Content (%)',
            type: 'number',
            isEditable: true,
            col: 4,
            validation: { required: true }
          },
          {
            label: 'Quantity Available',
            value: `${summary.total_available_quantity} ${summary.unit_label}`,
            isEditable: false,
            col: 4
          },
          {
            name: 'transferred_quantity',
            label: 'Quantity to Transfer',
            type: 'number',
            isEditable: true,
            col: 4,
            validation: {
              required: true,
              max: summary.total_available_quantity,
              min: 0.01
            }
          }
        ]
      }
    ];
  };

  return (
    <Container>
      <Row>
        <Card className="mb-3">
          <Card.Header>
            <FalconCardHeader
              title={header}
              className="ps-lg-4"
              titleTag="h5"
              titleClass="mb-0"
              light
            />
          </Card.Header>
          <Card.Body>
            {!isEmpty(formData) && (!procurementsParam || summaryLoaded) && (
              <DynamicFormV2
                data={formData}
                header={true}
                onSubmit={handleFormSubmit}
                mode={isEditMode && 'edit'}
                // Removed customSections to allow metadata-defined order
              />
            )}
          </Card.Body>
        </Card>
      </Row>
    </Container>
  );
}

export default TransferV2;
